import { Form, Input, TreeSelect, Upload, Button, message } from "antd";
import { useForm } from "antd/es/form/Form";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./CreateMessage.scss";
import { MenuProps } from "./types";
import { convertAllFilesToApiFormat } from "./utils/convertToBase64";
import TiptapEditor from "./utils/TiptapEditor";

import fetchAdvisorsData from "../leadsManagement/utils/fetchAdvisorsData";
import { BranchInfoType } from "../leadsManagement/types";
import { header } from "../utils/salesAdminHeader";
import { fileProps, imageProps } from "./utils/UploadProps";

import ContentTitle from "../../../components/ui/ContentTitle";
import { apiCall } from "../../../utils/apiCall";
import requiredRule from "../../../utils/formRules/requiredRule";
import GeneralModal from "../../../utils/GeneralModal";

//Permission usage
import { useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { ServiceTypes } from "../../../types/types";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

const CreateMessage = ({ setSelectedService, setSelectSubItem }: MenuProps) => {
  const [advisors, setAdvisors] = useState<BranchInfoType[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [treeDataOptions, setTreeDataOptions] = useState<any>([]);
  const [selectedAdvisors, setSelectedAdvisors] = useState<string[]>([]);
  const navigate = useNavigate();
  const [form] = useForm();

  const currentUser = useReactiveVar(__currentUser__);

  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const handleTreeSelectChange = (value: any) => {
    const expandedValue = value.flatMap((item: string) =>
      item.startsWith("HQ")
        ? advisors
            .find((branch) => branch.branchCode === item)
            ?.advisors.map((advisor) => advisor.companyNumber)
        : [item]
    );
    setSelectedAdvisors(expandedValue);
  };

  const stripHTML = (html: string): string => {
    return html.replace(/<[^>]+>/g, "").replace(/\s+/g, "");
  };

  const title = Form.useWatch("title", form);
  const content = Form.useWatch("content", form);

  const hasValidContent =
    !!content &&
    /<[^>]+>(.*?)<\/[^>]+>/.test(content) &&
    stripHTML(content).trim().length > 0;

  const isFormReady = !!title && hasValidContent && selectedAdvisors.length > 0;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const imageFiles = form.getFieldValue("images") || [];
      const attachmentFiles = form.getFieldValue("files") || [];

      const { images, files } = await convertAllFilesToApiFormat(
        imageFiles,
        attachmentFiles
      );

      const payload = {
        companyNumbers: selectedAdvisors,
        title: values.title,
        body: values.content,
        ...(images.length > 0 && { images }),
        ...(files.length > 0 && { files }),
      };
      await apiCall({
        method: "POST",
        header: header,
        payload: payload,
        endPoint: process.env.REACT_APP_SALES_ADMIN + "/contents/admin/mails",
      });
      setModalOpen(true);
    } catch (err) {
      console.log("error", err);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>업로드</div>
    </button>
  );

  useEffect(() => {
    fetchAdvisorsData(header, setAdvisors);
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("업무 관리");
  }, []);

  useEffect(() => {
    if (advisors.length > 0) {
      const treeData = advisors.map((branchInfo) => {
        return {
          title: `${branchInfo.branchName}(${branchInfo.branchCode})`,
          value: branchInfo.branchCode,
          key: branchInfo.branchCode,
          children: branchInfo.advisors.map((ads) => {
            return {
              title: `${ads.name}(${ads.companyNumber})`,
              value: ads.companyNumber,
              key: ads.companyNumber,
            };
          }),
        };
      });
      setTreeDataOptions(treeData);
    }
  }, [advisors]);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Business Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="createMessageWrapper">
      <ContentTitle
        title="메세지함"
        returnTitle="메세지함"
        onReturn={() => {
          navigate(-1);
        }}
      />
      <div className="content-container">
        <Form form={form} wrapperCol={{ span: 16 }} labelCol={{ span: 5 }}>
          <Form.Item name="receiver" label="수신자" rules={[requiredRule]}>
            <TreeSelect
              treeData={treeDataOptions}
              treeCheckable={true}
              onChange={handleTreeSelectChange}
              showCheckedStrategy={TreeSelect.SHOW_PARENT}
              allowClear
              placeholder="수신자를 선택해주세요."
            />
          </Form.Item>
          <Form.Item name="title" label="제목" rules={[requiredRule]}>
            <Input placeholder="알람 제목이 여기 들어갑니다." />
          </Form.Item>
          <Form.Item
            label="내용"
            required
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 16 }}
          >
            <TiptapEditor
              value={form.getFieldValue("content")}
              onChange={(html) => form.setFieldsValue({ content: html })}
            />
            <Form.Item
              name="content"
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    const hasMeaningfulContent =
                      !!value &&
                      /<[^>]+>(.*?)<\/[^>]+>/.test(value) &&
                      stripHTML(value).trim().length > 0;

                    return hasMeaningfulContent
                      ? Promise.resolve()
                      : Promise.reject(new Error("내용을 입력해주세요."));
                  },
                },
              ]}
              valuePropName="value"
              getValueFromEvent={(v) => v}
              noStyle
            >
              <Input hidden />
            </Form.Item>
          </Form.Item>
          <Form.Item
            label="이미지"
            name="images"
            valuePropName="fileList"
            getValueFromEvent={(e) =>
              Array.isArray(e?.fileList) ? e.fileList : []
            }
          >
            <Upload
              name="avatar"
              listType="picture-card"
              className="avatar-uploader"
              {...imageProps}
            >
              {uploadButton}
            </Upload>
          </Form.Item>
          <div className="text-xs leading-relaxed space-y-1 reminder-block">
            <div className="flex items-start gap-2">
              <span className="mt-[6px] w-1 h-1 rounded-full bg-black/45 flex-shrink-0" />
              <span className="reminder-text">
                최대 파일 크기: 1MB (tinypng.com을 사용하여 압축하는 것을 권장)
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-[6px] w-1 h-1 rounded-full bg-black/45 flex-shrink-0" />
              <span className="reminder-text">추천 너비: 600px</span>
            </div>
          </div>

          <Form.Item
            label="파일"
            name="files"
            valuePropName="fileList"
            getValueFromEvent={(e) =>
              Array.isArray(e?.fileList) ? e.fileList : []
            }
          >
            <Upload {...fileProps}>
              <Button icon={<UploadOutlined />}>업로드하려면 클릭</Button>
            </Upload>
          </Form.Item>
        </Form>
        <div className="footer">
          <Button onClick={() => {}}>취소</Button>
          <Button
            className="ml-4"
            type="primary"
            disabled={!isFormReady}
            onClick={() => {
              form
                .validateFields()
                .then(() => {
                  handleSubmit();
                })
                .catch((e) => console.log(e));
            }}
          >
            저장
          </Button>
        </div>
      </div>
      <GeneralModal
        open={modalOpen}
        close={() => {
          navigate("/sales188/message-box");
        }}
        icon={
          <img
            alt="checked icon"
            src={require("../../../assets/icons/green-check-circle.png")}
            style={{ width: 22, height: 22, marginRight: 16 }}
          />
        }
        width={400}
        isDeletion={false}
        isConfirm
        showNoBtn={false}
        confirmBtnText="확인"
        title="메세지가 발송되었습니다."
        text="발송된 내역은 리스트 화면에서 확인할 수 있습니다."
        handleConfirm={() => {
          navigate("/sales188/message-box");
        }}
      />
    </div>
  );
};

export default CreateMessage;
